import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

type Category = "kapasite" | "tuketim" | "ozellik";
const CAT_LABELS: Record<Category, string> = { kapasite: "Kapasite", tuketim: "Tüketim", ozellik: "Özellik" };
const CAT_OPTS = Object.entries(CAT_LABELS) as [Category, string][];

interface Props {
  open: boolean;
  locationId: string;
  locationName: string;
  /** "unite" | "makina" — başlık için */
  kind?: "unite" | "makina";
  onClose: () => void;
}

/**
 * Bir ünite/makinanın serbest özelliklerini (ad = değer + birim) yönetir — ekle/düzenle/sil.
 * Örn. Band ölçüsü = 1000 mt · Kapasite = 500 adet/saat · Güç = 2 kWh.
 */
export function EquipmentAttrDrawer({ open, locationId, locationName, kind, onClose }: Props) {
  const qc = useQueryClient();
  const attrsQ = trpc.equipmentAttr.list.useQuery({ locationId }, { enabled: open && !!locationId, staleTime: 10_000 });
  const createMut = trpc.equipmentAttr.create.useMutation();
  const updateMut = trpc.equipmentAttr.update.useMutation();
  const deleteMut = trpc.equipmentAttr.delete.useMutation();

  const [editId, setEditId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [category, setCategory] = useState<Category>("ozellik");
  const [err, setErr] = useState<string | null>(null);

  const attrs = (attrsQ.data ?? []) as Array<{
    id: string; label: string; value: string; unit: string | null; category: Category; sortOrder: number;
  }>;
  const busy = createMut.isPending || updateMut.isPending;

  function resetForm() {
    setEditId(null); setLabel(""); setValue(""); setUnit(""); setCategory("ozellik"); setErr(null);
  }
  useEffect(() => { if (open) resetForm(); /* eslint-disable-next-line */ }, [open, locationId]);

  function loadForEdit(a: (typeof attrs)[number]) {
    setEditId(a.id); setLabel(a.label); setValue(a.value); setUnit(a.unit ?? ""); setCategory(a.category); setErr(null);
  }

  async function save() {
    setErr(null);
    if (!label.trim()) { setErr("Özellik adı zorunlu."); return; }
    if (!value.trim()) { setErr("Değer zorunlu."); return; }
    const payload = { label: label.trim(), value: value.trim(), unit: unit.trim() || null, category };
    try {
      if (editId) await updateMut.mutateAsync({ id: editId, ...payload });
      else await createMut.mutateAsync({ locationId, ...payload });
      await qc.invalidateQueries();
      resetForm();
    } catch (e) { setErr((e as Error).message); }
  }

  function remove(id: string) {
    deleteMut.mutate({ id }, {
      onError: (e) => setErr(e.message),
      onSuccess: () => { deleteMut.reset(); qc.invalidateQueries(); if (editId === id) resetForm(); },
    });
  }

  const subtitle = locationName ? `${kind === "makina" ? "Makina" : kind === "unite" ? "Ünite" : "Ekipman"}: ${locationName}` : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Ekipman Özellikleri"
      subtitle={subtitle}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {/* Mevcut özellikler */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginBottom: 8 }}>
        Tanımlı Özellikler ({attrs.length})
      </div>
      {attrsQ.isLoading ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : attrs.length === 0 ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>
          Henüz özellik yok. Aşağıdan ekleyin (ör. Band ölçüsü = 1000 mt, Kapasite = 500 adet/saat).
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {attrs.map((a) => (
            <div key={a.id} className="d-card" style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span>{a.label}</span>
                  <span style={{ fontFamily: "var(--font-mono, monospace)", fontVariantNumeric: "tabular-nums", color: "var(--primary)" }}>
                    {a.value}{a.unit ? ` ${a.unit}` : ""}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>{CAT_LABELS[a.category]}</div>
              </div>
              <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => loadForEdit(a)}><Icon name="edit" size={14} /></button>
              <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => remove(a.id)} disabled={deleteMut.isPending}><Icon name="trash" size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Ekle / düzenle formu */}
      <div style={{ borderTop: "1px solid var(--neutral-200)", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{editId ? "Özelliği Düzenle" : "Yeni Özellik"}</div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="ea-label">Özellik Adı <span className="req">*</span></label>
            <input id="ea-label" className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="örn. Band ölçüsü" maxLength={120} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="ea-cat">Kategori</label>
            <select id="ea-cat" className="form-select" value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {CAT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="ea-value">Değer <span className="req">*</span></label>
            <input id="ea-value" className="form-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="örn. 1000" maxLength={200} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="ea-unit">Birim</label>
            <input id="ea-unit" className="form-input" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="mt, kWh, adet/saat…" maxLength={30} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="button" className="btn-md primary" onClick={save} disabled={busy}>
            <Icon name={editId ? "check" : "plus"} size={14} />{busy ? "Kaydediliyor…" : editId ? "Güncelle" : "Ekle"}
          </button>
          {editId ? <button type="button" className="btn-md ghost" onClick={resetForm} disabled={busy}>Vazgeç</button> : null}
        </div>
      </div>
    </Drawer>
  );
}
